import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { RewardStatsResponse, CurrencyStats } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

/** 리워드 종류 한국어 매핑 */
const REWARD_TYPE_LABELS: Record<string, string> = {
  VOTE: '투표',
  VOTE_AD_BONUS: '투표광고보너스',
  DAILY_BONUS: '일일 보너스',
  DAILY_AD_BONUS: '오늘의투표광고보너스',
  WEEKLY_MILESTONE_1: '주간 1단계',
  WEEKLY_MILESTONE_2: '주간 2단계',
  WEEKLY_MILESTONE_3: '주간 3단계',
  CREATOR_MILESTONE: '설문 작성 보너스',
  FIRST_VOTE_BONUS: '첫 투표 보너스',
  MILESTONE_AD_BONUS: '누적투표보너스',
  ATTENDANCE: '출석',
  ATTENDANCE_AD_BONUS: '출석광고보너스',
  ATTENDANCE_STREAK: '연속출석보너스',
  ATTENDANCE_STREAK_SAVE: '연속출석유지보너스',
  COMMENT: '댓글 보상',
  COMMENT_AD_BONUS: '댓글 광고보너스',
  SURVEY_LIKE: '설문 좋아요 보상',
  SURVEY_LIKE_AD_BONUS: '설문 좋아요 광고보너스',
  COMMENT_LIKE: '댓글 좋아요 보상',
  COMMENT_LIKE_AD_BONUS: '댓글 좋아요 광고보너스',
  WEEKLY_MISSION: '이번주 미션 완료',
  WEEKLY_MISSION_AD_BONUS: '이번주 미션 광고보너스',
};

/** 기간 필터 옵션 */
const PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

export const RewardStatsChart = () => {
  const [period, setPeriod] = useState('30D');

  const { data, isLoading } = useQuery({
    queryKey: ['adminRewardStats', period],
    queryFn: () => rewardApi.getRewardStats(period).then(res => res.data.results as RewardStatsResponse),
  });

  if (isLoading || !data) {
    return <div className="text-center py-8 text-gray-400">로딩 중...</div>;
  }

  return (
    <div className="space-y-8">
      {/* 기간 필터 (공통) */}
      <div className="flex justify-end">
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      {/* 조각 지급 추이 */}
      <CurrencySection
        title="조각 지급 추이"
        stats={data.piece}
        lineColor="#f59e0b"
        barColor="#f59e0b"
        unit="개"
      />

      {/* 토스포인트 지급 추이 */}
      <CurrencySection
        title="토스포인트 지급 추이"
        stats={data.tossPoint}
        lineColor="#3b82f6"
        barColor="#3b82f6"
        unit="원"
      />
    </div>
  );
};

/** 통화별 차트 섹션 */
const CurrencySection = ({
  title,
  stats,
  lineColor,
  barColor,
  unit,
}: {
  title: string;
  stats: CurrencyStats;
  lineColor: string;
  barColor: string;
  unit: string;
}) => {
  const dailyData = stats.dailyTrend.map(d => ({
    ...d,
    date: d.date.length > 5 ? d.date.slice(5) : d.date, // YYYY-MM-DD → MM-DD
  }));

  const typeData = stats.byType.map(d => ({
    ...d,
    type: REWARD_TYPE_LABELS[d.rewardType] ?? d.rewardType,
  }));

  return (
    <div>
      <h2 className="text-base font-bold text-gray-900 mb-4">{title}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">일별 지급</h3>
            {dailyData.length === 0 ? (
              <div className="text-center py-8 text-gray-400">데이터가 없습니다.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => [`${Number(value).toLocaleString()}${unit}`, '지급량']}
                    labelFormatter={label => `${label}`}
                  />
                  <Line type="monotone" dataKey="amount" stroke={lineColor} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">종류별 비교</h3>
            {typeData.length === 0 ? (
              <div className="text-center py-8 text-gray-400">데이터가 없습니다.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'totalAmount' ? `${Number(value).toLocaleString()}${unit}` : `${value}건`,
                      name === 'totalAmount' ? '수량' : '건수',
                    ]}
                  />
                  <Bar dataKey="totalAmount" fill={barColor} name="totalAmount" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalCount" fill="#10b981" name="totalCount" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

/** 기간 필터 버튼 그룹 */
const PeriodFilter = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <div className="flex gap-1">
    {PERIOD_OPTIONS.map(opt => (
      <Button
        key={opt.value}
        variant={value === opt.value ? 'default' : 'outline'}
        size="sm"
        className="text-xs h-7 px-2"
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </Button>
    ))}
  </div>
);
